import { Button, Container, Grid, GridColumn, Heading, IFormHelperProps, Separator, Text, TextField } from 'telia-front-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import HiddenCaptcha, { HiddenCaptchaRef } from '../components/HiddenCaptcha';
import UiNotice from '../components/UiNotice';
import useStores from '../stores/UseStores';
import { EMAIL_FIELD_ID, emailFieldHelper, getHelperErrorMessage, helperInErrorState } from '../util/FieldHelperUtil';
import { isEmailValid } from '../util/ValidationUtil';

const EmailForm = observer((): React.JSX.Element => {
  const {
    addEmailStore,
    translateStore: { translate },
    uiStore: { showErrorNoticeWithErroredFields },
    languageStore: { currentLanguage },
  } = useStores();
  const { email, fetching, response } = addEmailStore;
  const recaptchaRef = React.createRef<HiddenCaptchaRef>();
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);

  const emailFormHelper = (forceIsDirty?: boolean) => emailFieldHelper(translate, email, forceIsDirty ?? hasSubmitted);

  const setEmailInStore = React.useCallback((emailValue: string) => {
    addEmailStore.setEmail(emailValue);
  }, []);

  const handleEmailOnBlur = React.useCallback(() => {
    addEmailStore.setResponse(null);
  }, []);

  const handleFormSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setHasSubmitted(true);
    if (email && !isInvalid()) {
      const token = await recaptchaRef?.current?.getCaptchaAsync();
      addEmailStore.handleSubmit(token);
    } else {
      showErrorNoticeWithErroredFields('form.error', [emailFormHelper(true) || getEmailResponseErrorHelper()]);
    }
  };

  const getEmailResponseErrorHelper = (): IFormHelperProps | undefined => {
    if (response?.emailError) {
      return { type: 'error', text: translate(response.emailError), id: EMAIL_FIELD_ID };
    }
    return undefined;
  };

  const isInvalid = (): boolean => {
    return !isEmailValid(email) || !!getEmailResponseErrorHelper();
  };

  return (
    <Container>
      <UiNotice />
      <Text>
        <form onSubmit={handleFormSubmit} id="emailLogin">
          <Heading element={'h4'} className={'text-center'}>
            {translate('add_email.header')}
          </Heading>
          <Separator spacing={'small'} color={'invisible'} />
          <Grid align={['center-sm']}>
            <GridColumn width={['sm-8', 'md-6', 'lg-4']}>
              <Text>
                <p>{translate('email_register.email_title')}</p>
                <TextField
                  label={translate('email_register.email_label')}
                  id={EMAIL_FIELD_ID}
                  value={email}
                  onChange={setEmailInStore}
                  isInvalid={helperInErrorState(emailFormHelper()) || !!getEmailResponseErrorHelper()}
                  errorMessage={getHelperErrorMessage(emailFormHelper()) || getHelperErrorMessage(getEmailResponseErrorHelper())}
                  onBlur={handleEmailOnBlur}
                  required={true}
                  input={{ autoComplete: 'email' }}
                />
                <HiddenCaptcha ref={recaptchaRef} currentLanguage={currentLanguage} />
                <Separator color="invisible" spacing="small" />
                <Button
                  text={translate('email_register.email_button')}
                  iconRight="#arrow-right"
                  block={true}
                  onClick={handleFormSubmit}
                  isLoading={fetching}
                  disabled={fetching}
                />
              </Text>
            </GridColumn>
          </Grid>
        </form>
      </Text>
    </Container>
  );
});

export default EmailForm;
